// =============================================================================
// Subscriptions.tsx
// Plan activo + comparativa de planes (cards) + comprobantes.
// =============================================================================

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import {
  Check,
  Loader2,
  CreditCard,
  Calendar,
  FileText,
  RefreshCw,
  Tag,
  ChevronDown,
  Gift,
  Users,
  CheckCircle,
  Sparkles,
  BadgeCheck,
  Gem,
  Award,
  XCircle,
  Undo2,
} from 'lucide-react';
import Joyride from 'react-joyride';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { GLASS_CARD } from '@/components/ui/glass';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import SubscriptionsTourSteps from '@/components/tour/company/SubscriptionsTourSteps';
import {
  useSubscripcion,
  type PlanAgrupado,
} from '@/components/hooks/useSubscripcion';
import { contratarPlan, cancelarPlan, reactivarPlan, cancelarCambioPlan } from '@/components/hooks/useContratarPlan';
import { abrirPortalCliente } from '@/components/hooks/useMetodoPago';
import {
  cargarListaTrabajadores,
  useTrabajadores,
  type TrabajadorLista,
} from '@/components/hooks/useTrabajadores';
import {
  cargarPromocionEmpresa,
  formatearTextoPromocionEmpresa,
  type EmpresaPromocionActiva,
} from '@/components/hooks/usePromocion';
import { useFlags } from '@/components/hooks/useFlags';
import { getTourStyles } from '@/components/tour/tourStyles';

// ─── Animations ───────────────────────────────────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

// Superficie interna neutra reutilizable (filas dentro de cards)
const INNER_ROW =
  'border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 rounded-lg';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatFecha = (fecha: string | null | undefined) => {
  if (!fecha) return '—';
  // 'YYYY-MM-DD' con new Date() se interpreta como medianoche UTC y al
  // formatear a hora local (España, UTC+1/+2) cae en el día siguiente
  // ("31 dic" → "1 ene"). Partiendo la cadena se construye como fecha
  // local y se muestra el día correcto.
  const [y, m, d] = fecha.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

// Abre una URL externa de Stripe (Checkout o Billing Portal).
//
// En web basta con navegar / abrir pestaña. En la app nativa NO: un
// window.location.href se llevaría la WebView entera a billing.stripe.com
// (el usuario sale de la app, y la success_url le devolvería a la versión
// web, no al panel nativo), y window.open tiene comportamiento
// inconsistente según la configuración de la WebView. Browser.open muestra
// una Custom Tab encima de la app y, al cerrarse, se vuelve justo donde se
// estaba.
async function abrirUrlStripe(url: string, nuevaPestana = false) {
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url, presentationStyle: 'popover' });
    return;
  }
  if (nuevaPestana) {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    window.location.href = url;
  }
}

// promoDescuentoPct: 100 (o null en trial gratis) = plan a 0€ mientras dure
// la promo. Sustituye al antiguo hasPromo() por plan, ya que ahora el
// descuento es global (viene de la promo activa, no de cada plan).
function precioConPromo(
  precioMensual: number,
  promo: EmpresaPromocionActiva | null
): { precio: number; conDescuento: boolean } {
  if (!promo) return { precio: precioMensual, conDescuento: false };

  const pct = promo.descuento_pct ?? 100; // sin descuento_pct explícito = 100% gratis
  const precio = Math.round(precioMensual * (1 - pct / 100) * 100) / 100;
  return { precio, conDescuento: pct > 0 };
}

// ─── PlanCard ─────────────────────────────────────────────────────────────────

function PlanCard({
  plan,
  esActivo,
  isDestacado,
  promo,
  empresaId,
  onCambiado,
  open,
  onToggleOpen,
  idPlanPendiente,
  nombrePlanPendiente,
  fechaCambioPendiente,
  planActivo,
  descuentoFundadorPct,
  bajaProgramada,
  botonesActivos,
}: {
  plan: PlanAgrupado;
  esActivo: boolean;
  isDestacado: boolean;
  promo: EmpresaPromocionActiva | null;
  empresaId: string;
  onCambiado: () => void;
  open: boolean;
  onToggleOpen: () => void;
  idPlanPendiente: string | null;
  nombrePlanPendiente: string | null;
  fechaCambioPendiente: string | null;
  planActivo: ReturnType<typeof useSubscripcion>['planActivo'];
  descuentoFundadorPct: number;
  bajaProgramada: boolean;
  /** Interruptor global (flag_suscripciones_activas). Con false, ningún
      botón de esta card permite operar: solo se puede mirar. */
  botonesActivos: boolean;
}) {
  const [contratando, setContratando] = useState(false);
  const [cambiandoPlan, setCambiandoPlan] = useState(false);
  const enCurso = useRef(false);
  const { precio: precioTrasPromo, conDescuento: conDescuentoPromo } =
    precioConPromo(plan.precio_mensual, promo);

  // El descuento de fundador (de por vida) se aplica sobre el precio del
  // plan, además de cualquier promoción temporal que haya activa.
  const precio =
    descuentoFundadorPct > 0
      ? Math.round(precioTrasPromo * (1 - descuentoFundadorPct / 100) * 100) / 100
      : precioTrasPromo;
  const conDescuento = conDescuentoPromo || descuentoFundadorPct > 0;

  const categorias = plan.categorias.filter(
    (c) => c.categoria !== 'Precio adicional'
  );

  const handleContratar = async () => {
    if (enCurso.current) return; // bloqueo síncrono: ignora dobles clics
    enCurso.current = true;
    setContratando(true);
    try {
      const resp = await contratarPlan(empresaId, plan.id_plan);
      if (!resp) {
        toast.error('No se pudo iniciar la contratación. Inténtalo de nuevo.');
        return;
      }

      // La Edge Function rechaza el cambio si hay una baja programada:
      // primero hay que reactivar el plan (decisión explícita del cliente).
      // El botón ya sale deshabilitado en ese caso, pero la respuesta se
      // contempla igualmente — el estado puede haber cambiado desde otra
      // pestaña o desde el Billing Portal.
      if (resp.error) {
        toast.error(resp.error);
        if (resp.requiereReactivar) onCambiado(); // refresca para que la UI se ponga al día
        return;
      }

      if (resp.url) {
        // En nativo se abre en una Custom Tab encima de la app; el refresco
        // al volver lo dispara el listener 'browserFinished' del componente
        // principal, porque el checkout ocurre fuera de este JS.
        await abrirUrlStripe(resp.url);
        return;
      }

      if (resp.programado) {
        toast.success(
          `Tu plan actual sigue activo hasta el ${resp.fechaCambio ? new Date(resp.fechaCambio).toLocaleDateString('es-ES') : 'fin de tu periodo actual'} — después pasará automáticamente a ${plan.plan}.`
        );
        onCambiado();
        return;
      }

      // Cambio aplicado directamente sobre la suscripción existente. El
      // mensaje cambia según el caso: en trial no se cobra nada todavía, y
      // una bajada puede haber dado de baja trabajadores que el plan nuevo
      // ya no cubre — conviene decirlo, no que lo descubra en la lista.
      const cancelados = (resp.trabajadoresCancelados ?? 0) + (resp.plazasLiberadas ?? 0);

      if (resp.enTrial) {
        toast.success(
          cancelados > 0
            ? `Cambiado a ${plan.plan}. Se ha dado de baja a ${cancelados} trabajador${cancelados > 1 ? 'es' : ''} que el plan nuevo no cubre. Seguirás sin pagar hasta que termine tu promoción.`
            : `Cambiado a ${plan.plan} — seguirás sin pagar nada hasta que termine tu promoción.`
        );
      } else {
        toast.success(
          cancelados > 0
            ? `Cambiado a ${plan.plan}. Se ha cobrado la diferencia proporcional y se ha dado de baja a ${cancelados} trabajador${cancelados > 1 ? 'es' : ''} que el plan nuevo no cubre.`
            : `Cambiado a ${plan.plan} — se ha cobrado ya la diferencia proporcional. La factura llegará en unos segundos.`
        );
      }
      onCambiado();
    } finally {
      enCurso.current = false;
      setContratando(false);
    }
  };

  const handleCancelarCambio = async () => {
    if (enCurso.current) return;
    enCurso.current = true;
    setContratando(true);
    try {
      const resp = await cancelarCambioPlan(empresaId);
      if (!resp.ok) {
        toast.error('No se pudo deshacer el cambio de plan. Inténtalo de nuevo.');
        return;
      }
      toast.success(`Cambio cancelado — te quedas en ${plan.plan} sin ningún cambio pendiente.`);
      onCambiado();
    } finally {
      enCurso.current = false;
      setContratando(false);
    }
  };

  return (
    <div
      className={`relative flex flex-col h-full rounded-2xl overflow-hidden transition-shadow ${
        isDestacado
          ? 'border-2 border-[#103254] dark:border-blue-500 shadow-lg'
          : 'border border-gray-200 dark:border-white/10 shadow-sm'
      } bg-white dark:bg-[#1c1c1e]`}
    >
      {/* Barra superior */}
      <div
        className={`h-1 w-full shrink-0 ${
          isDestacado
            ? 'bg-gradient-to-r from-[#103254] to-[#288E7F]'
            : 'bg-gray-100 dark:bg-white/10'
        }`}
      />

      {/* Badge "Más completo" */}
      {isDestacado && (
        <div className="absolute top-4 right-4">
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#103254] text-white">
            Más completo
          </span>
        </div>
      )}

      {/* Header */}
      <div className="px-6 pt-6 pb-4 flex flex-col flex-1">
        {/* Nombre */}
        <div className="flex items-center gap-2 mb-3">
          <p
            className={
              esActivo
                ? 'text-lg font-bold text-gray-900 dark:text-white leading-none'
                : 'text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-none'
            }
          >
            {plan.plan}
          </p>
          {esActivo && (
            <span className="text-[10px] font-semibold text-[#103254] dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-[#103254]/20 dark:border-blue-400/20 px-2 py-0.5 rounded-full">
              Tu plan
            </span>
          )}
        </div>

        {/* Precio */}
        <div className="flex items-baseline gap-1.5 mb-1">
          {conDescuento && (
            <span className="text-sm font-medium text-gray-300 dark:text-gray-600 line-through">
              {plan.precio_mensual.toFixed(2).replace('.', ',')}€
            </span>
          )}
          <span className="text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
            {precio.toFixed(2).replace('.', ',')}€
          </span>
          <span className="text-sm text-gray-400 dark:text-gray-500">/mes</span>
        </div>

        {/* Descripción */}
        <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed mt-2 mb-3 min-h-[2.5rem]">
          {plan.descripcion_plan}
        </p>

        {/* CTA: informativo si ya es el plan actual, ámbar si hay un
            cambio pendiente, o real para iniciar el checkout de Stripe.
            Durante la promo también se puede contratar — se aplica el
            cupón de Stripe (si está configurado), cobra 0€ ahora y pasa
            a precio real solo cuando caduque el cupón, sin que la
            empresa tenga que hacer nada.
            mt-auto ancla el CTA al fondo del header para que quede a la
            misma altura en todas las cards, independientemente de la
            longitud de la descripción de cada plan. */}
        <div className="mt-auto">
        {esActivo && idPlanPendiente ? (
          <Button
            onClick={handleCancelarCambio}
            disabled={contratando || !botonesActivos}
            variant="outline"
            className="w-full rounded-xl border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 mt-1"
          >
            <Undo2 className="w-4 h-4 mr-2" />
            {contratando ? 'Deshaciendo…' : `Cancelar cambio a ${nombrePlanPendiente ?? 'otro plan'}`}
          </Button>
        ) : esActivo ? (
          <Button
            variant={planActivo?.auto_renovacion ? "destructive" : "outline"}
            disabled={cambiandoPlan || !botonesActivos}
            onClick={async () => {
              try {
                setCambiandoPlan(true);

                const resp = planActivo?.auto_renovacion
                  ? await cancelarPlan(empresaId)
                  : await reactivarPlan(empresaId);

                if (resp.ok) {
                  onCambiado();
                }
              } finally {
                setCambiandoPlan(false);
              }
            }}
            className="w-full rounded-xl mt-1"
          >
            {cambiandoPlan ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {planActivo?.auto_renovacion
                  ? "Cancelando..."
                  : "Reactivando..."}
              </>
            ) : planActivo?.auto_renovacion ? (
              <>
                <XCircle className="w-4 h-4 mr-2" />
                Cancelar plan
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reactivar plan
              </>
            )}
          </Button>
        ) : idPlanPendiente === plan.id_plan ? (
          <button
            disabled
            className="w-full flex items-center justify-center gap-2 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-sm font-medium py-2.5 cursor-not-allowed mt-1"
          >
            <Calendar className="w-3.5 h-3.5" />
            {fechaCambioPendiente
              ? `Empieza el ${new Date(fechaCambioPendiente).toLocaleDateString('es-ES')}`
              : 'Programado'}
          </button>
        ) : bajaProgramada ? (
          // Con una baja programada no se puede cambiar de plan: el cambio
          // dejaría el plan nuevo heredando la cancelación (contratado pero
          // cancelándose igual) o borraría la baja sin que el cliente la
          // haya deshecho. Primero reactivar, luego cambiar.
          <button
            disabled
            title="Reactiva tu plan actual antes de cambiar de plan"
            className="w-full flex items-center justify-center gap-2 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-sm font-medium py-2.5 cursor-not-allowed mt-1"
          >
            <XCircle className="w-3.5 h-3.5" />
            Reactiva tu plan para cambiarlo
          </button>
        ) : (
          <>
          <button
            onClick={handleContratar}
            disabled={contratando || !botonesActivos}
            style={{ backgroundColor: '#288E7F' }}
            className="w-full flex items-center justify-center rounded-xl mt-1 text-white text-sm font-medium py-2.5 hover:opacity-90 disabled:opacity-60"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {contratando ? 'Abriendo pago…' : 'Contratar este plan'}
          </button>
          {/* Informacion previa a la contratacion (LSSI art. 27): que se
              renueva sola y donde estan las condiciones. */}
          <p className="mt-2 text-[11px] leading-snug text-gray-400 dark:text-gray-500 text-center">
            Suscripción mensual con renovación automática hasta que la
            canceles. Impuestos detallados antes de pagar. Al contratar
            aceptas los{' '}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600 dark:hover:text-gray-300"
            >
              Términos y Condiciones
            </a>
            .
          </p>
          </>
        )}
        </div>
      </div>

      {/* Separador */}
      <div className="mx-6 border-t border-gray-100 dark:border-white/10" />

      {/* Precio de trabajador adicional (informativo — el alta real se
          hace desde el menú de Trabajadores, no desde aquí).
          Cuando el plan NO vende trabajadores adicionales se reserva el
          mismo hueco vacío, para que la fila de "Ver funcionalidades"
          quede a la misma altura en todas las cards. */}
      {plan.precio_trabajador_adicional > 0 ? (
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Users className="w-3.5 h-3.5 shrink-0" />
            Trabajador adicional
          </div>
          <span className="text-sm font-semibold text-[#103254] dark:text-blue-400">
            {plan.precio_trabajador_adicional.toFixed(2).replace('.', ',')}€
            <span className="text-xs font-normal text-gray-400 dark:text-gray-500 ml-0.5">
              /mes
            </span>
          </span>
        </div>
      ) : (
        <div className="px-6 py-3 flex items-center justify-between" aria-hidden="true">
          <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-600">
            <Users className="w-3.5 h-3.5 shrink-0" />
            Sin trabajadores adicionales
          </div>
          <span className="text-sm font-semibold text-gray-300 dark:text-gray-600">
            —
          </span>
        </div>
      )}

      {/* Toggle features */}
      <button
        onClick={onToggleOpen}
        className="mx-6 mb-4 flex items-center justify-between text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors py-2"
      >
        <span>{open ? 'Ocultar funcionalidades' : 'Ver funcionalidades'}</span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-4 h-4" />
        </motion.div>
      </button>

      {/* Features expandibles */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 space-y-4">
              {categorias.map((cat) => (
                <div key={cat.id_categoria_saas}>
                  <p className="text-[10px] font-bold text-[#288E7F] uppercase tracking-widest mb-2">
                    {cat.categoria}
                  </p>
                  <ul className="space-y-2">
                    {cat.servicios.map((s) => (
                      <li
                        key={s.id_servicio_saas}
                        className="flex items-start gap-2.5"
                      >
                        <Check className="w-3.5 h-3.5 text-[#288E7F] shrink-0 mt-0.5" />
                        <div>
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {s.servicio_saas}
                          </span>
                          {s.descripcion_corta && (
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                              {s.descripcion_corta}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Component principal ──────────────────────────────────────────────────────

import HistorialPagosPanel from '@/components/pages/company/HistorialPagosPanel';

export default function Subscriptions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const empresaId = localStorage.getItem('id_empresa') || '';
  const [runTour, setRunTour] = useState(false);
  const [promo, setPromo] = useState<EmpresaPromocionActiva | null>(null);
  const [listaTrabajadores, setListaTrabajadores] = useState<TrabajadorLista[]>([]);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [cambiandoMetodoPago, setCambiandoMetodoPago] = useState(false);
  const [funcionalidadesAbiertas, setFuncionalidadesAbiertas] = useState(false);
  const [cambiandoAutoRenovacion, setCambiandoAutoRenovacion] = useState(false);
  const cambiandoAutoRenovacionRef = useRef(false);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);

  // Anclaje de trabajador cuando se llega desde la pantalla de Trabajadores
  // con ?trabajador=<id_usuario>: refs por fila para hacer scroll y un
  // estado que marca cuál resaltar temporalmente.
  const trabajadorRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [trabajadorResaltado, setTrabajadorResaltado] = useState<string | null>(null);

  const { habilitarAcceso, deshabilitarAcceso, cambiandoAcceso } =
    useTrabajadores();

  // Interruptor global de la pantalla: mientras `flag_suscripciones_activas`
  // esté a false en internal_config, todo se ve pero no se puede operar.
  // Por defecto apagado — si la lectura del flag falla, no se contrata nada.
  const { activo: flagActivo, loading: cargandoFlags } = useFlags();
  const botonesActivos = flagActivo('flag_suscripciones_activas');

  const {
    planActivo,
    planEfectivo,
    planes,
    comprobantes,
    movimientos,
    loading,
    loadingComprobante,
    descargarComprobante,
    fetchComprobantes,
    fetchMovimientos,
    fetchPlanActivo,
    fetchPlanEfectivo,
  } = useSubscripcion(empresaId);

  // El botón global de ayuda (arriba a la derecha) lanza el tour de esta
  // página. Se oculta mientras carga, que es cuando no hay nada que enseñar.
  usePageHelp(() => setRunTour(true), !loading);
  useTourVisibility(runTour);

  // Promo activa: determina si el plan está a 0€/con descuento ahora mismo.
  // OJO: se consulta la promo de ESTA empresa (empresa_promociones), no la
  // promo global que se asigna a los registros nuevos ahora mismo.
  useEffect(() => {
    if (!empresaId) return;
    cargarPromocionEmpresa(empresaId).then(setPromo);
  }, [empresaId]);

  // Listado con estado de acceso, para el switch de habilitar/deshabilitar.
  // Orden: gratis (incluido en cupo o promo) primero, luego los que
  // facturan, y al final los que no tienen nada contratado — así el
  // primero de la lista siempre coincide con el que sale gratis, en vez
  // de depender de a quién se habilitó primero cronológicamente.
  const ordenarTrabajadores = (lista: TrabajadorLista[]) => {
    const prioridad = (t: TrabajadorLista) => {
      if (t.es_incluido_plan || t.cubierto_por_promo) return 0; // gratis
      if (t.tiene_acceso) return 1; // facturando
      return 2; // sin contratar
    };
    return [...lista].sort((a, b) => prioridad(a) - prioridad(b));
  };

  const recargarListaTrabajadores = () => {
    if (!empresaId) return;
    setCargandoLista(true);
    cargarListaTrabajadores(empresaId)
      .then((lista) => setListaTrabajadores(ordenarTrabajadores(lista)))
      .finally(() => setCargandoLista(false));
  };

  useEffect(() => {
    recargarListaTrabajadores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  // Refresco completo tras una operación que toca plan, trabajadores y/o
  // facturación. El segundo fetchComprobantes con retardo es porque la
  // factura la crea el webhook de Stripe unos segundos después.
  const refrescarTodo = () => {
    fetchPlanActivo();
    fetchPlanEfectivo();
    recargarListaTrabajadores();
    fetchComprobantes();
    setTimeout(() => fetchComprobantes(), 5000);
  };

  // ── Vuelta desde Stripe en la app nativa ────────────────────────────────
  // El Checkout y el Billing Portal se abren en una Custom Tab, fuera de la
  // WebView: cuando el usuario la cierra, este JS no se ha enterado de nada
  // (no hay recarga de página ni success_url que aterrice aquí). El evento
  // 'browserFinished' es el único aviso fiable, y además cubre el caso de
  // que abandone el pago a medias. En web no hace falta: la navegación de
  // vuelta remonta la pantalla.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !empresaId) return;

    const handle = Browser.addListener('browserFinished', () => {
      refrescarTodo();
    });

    return () => {
      handle.then((h) => h.remove()).catch(() => { /* plugin ya descargado */ });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  // Vuelta desde Stripe en WEB: la success_url trae ?checkout=ok. Se
  // refresca y se limpia el parámetro para que no se repita al navegar.
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (!checkout) return;

    if (checkout === 'ok') {
      toast.success('Pago completado — actualizando tu suscripción…');
      refrescarTodo();
    } else if (checkout === 'cancelado') {
      toast('Has salido del pago sin completarlo.');
    }

    searchParams.delete('checkout');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Al llegar con ?trabajador=<id_usuario> desde la pantalla de
  // Trabajadores: una vez cargada la lista, hacer scroll a esa fila,
  // resaltarla unos segundos y limpiar el query param para que no se
  // repita el efecto en recargas posteriores.
  useEffect(() => {
    const idTrabajador = searchParams.get('trabajador');
    if (!idTrabajador || cargandoLista || listaTrabajadores.length === 0) return;

    const el = trabajadorRefs.current[idTrabajador];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTrabajadorResaltado(idTrabajador);
      const timer = setTimeout(() => setTrabajadorResaltado(null), 3000);

      // Limpiar el param sin dejar entrada en el historial
      searchParams.delete('trabajador');
      setSearchParams(searchParams, { replace: true });

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, cargandoLista, listaTrabajadores]);

  const handleToggleAcceso = async (
    trabajador: TrabajadorLista,
    activar: boolean
  ) => {
    let generoFactura = false;

    if (activar) {
      const resp = await habilitarAcceso(trabajador);
      generoFactura = resp.generoFactura === true;
    } else {
      await deshabilitarAcceso(trabajador);
    }

    // Se recarga siempre, incluso si falló: un intento fallido puede haber
    // creado la fila en empresa_trabajadores antes de romperse en un paso
    // posterior (p. ej. la llamada a Stripe), y si no refrescamos aquí, el
    // próximo intento seguiría pensando que no existe fila y volvería a
    // intentar crearla, chocando con la clave única.
    recargarListaTrabajadores();
    fetchComprobantes(); // el alta puede haber generado un cobro nuevo

    if (generoFactura) {
      // El comprobante/factura de un trabajador facturado (fuera de cupo)
      // lo crea el webhook de Stripe al confirmar el cobro — eso llega
      // unos segundos después de esta llamada, no al instante. Solo
      // aplica cuando de verdad se ha creado un cobro (no para los que
      // quedan gratis por cupo del plan o por promo).
      toast('La factura puede tardar unos segundos en generarse y aparecer aquí.', {
        duration: 6000,
      });
      setTimeout(() => fetchComprobantes(), 5000);
    }
  };

  const handleToggleAutoRenovacion = async (activar: boolean) => {
    if (cambiandoAutoRenovacionRef.current) return; // bloqueo síncrono
    cambiandoAutoRenovacionRef.current = true;
    setCambiandoAutoRenovacion(true);
    try {
      const resp = activar
        ? await reactivarPlan(empresaId)
        : await cancelarPlan(empresaId);

      if (!resp.ok) {
        toast.error(
          activar
            ? 'No se pudo reactivar la auto-renovación.'
            : 'No se pudo programar la cancelación del plan.'
        );
        return;
      }

      toast.success(
        activar
          ? 'Auto-renovación reactivada — el plan sigue con normalidad'
          : 'Auto-renovación desactivada — el plan se mantiene activo hasta el final del periodo ya pagado'
      );
      fetchPlanActivo();
      fetchPlanEfectivo();
      recargarListaTrabajadores();
    } finally {
      cambiandoAutoRenovacionRef.current = false;
      setCambiandoAutoRenovacion(false);
    }
  };

  const handleCambiarMetodoPago = async () => {
    setCambiandoMetodoPago(true);
    try {
      const resp = await abrirPortalCliente(empresaId);
      if (!resp?.url) {
        toast.error('No se pudo abrir el portal de facturación. Inténtalo de nuevo.');
        return;
      }
      // En web, pestaña nueva (el usuario no pierde la pantalla). En nativo,
      // Custom Tab: window.open es inconsistente dentro de la WebView.
      await abrirUrlStripe(resp.url, true);
    } finally {
      setCambiandoMetodoPago(false);
    }
  };

  // De la empresa, no del plan: un fundador sin plan contratado también
  // debe detectarse, y planActivo sería null en ese caso.
  const esDiamante = planEfectivo?.es_fundador_diamante ?? false;
  const esPlatino = planEfectivo?.es_fundador_platino ?? false;
  // Se rige por el plan CONTRATADO, no el efectivo: no se dan de alta
  // trabajadores que el plan real no cubrirá al terminar la promoción.
  const planPermiteTrabajadorAdicional =
    (planEfectivo?.precio_trabajador_adicional_contratado ?? 0) > 0;
  const esPremium = esDiamante || esPlatino;
  const isMobile = window.innerWidth < 640;
  const { precio: precioPlanActivo, conDescuento: planActivoConDescuento } =
    precioConPromo(planActivo?.precio_mensual ?? 0, esDiamante ? null : promo);
  // Founder diamante siempre es 0€ de por vida, independientemente de la
  // promo general — no se le aplica precioConPromo con la promo activa.
  // Fundador: Diamante 100%, Platino 50%. Solo sobre el plan — los
  // trabajadores adicionales van siempre a precio normal.
  const descuentoFundadorPct = esDiamante ? 100 : esPlatino ? 50 : 0;

  const aplicarDescuentoFundador = (precio: number) =>
    descuentoFundadorPct > 0
      ? Math.round(precio * (1 - descuentoFundadorPct / 100) * 100) / 100
      : precio;

  const precioMostrado = aplicarDescuentoFundador(precioPlanActivo);

  // ── Estado real de la contratación ─────────────────────────────────────
  // Se calcula UNA vez y manda sobre cualquier otra etiqueta. Antes cada
  // rótulo lo deducía por su cuenta y la rama de promoción (por_promocion)
  // se evaluaba primero, así que con una promo activa la baja programada
  // quedaba invisible: seguía diciendo "Pasa a Premium" y "Activa" aunque
  // el plan estuviera cancelado.
  const bajaProgramada = planActivo?.auto_renovacion === false;
  const cambioPendiente = !!planActivo?.id_plan_pendiente;
  // Cobro fallido: Stripe está reintentando. El acceso sigue activo, pero
  // hay que avisar o el cliente se entera cuando ya se le ha cortado.
  const pagoFallido = planActivo?.stripe_status === 'past_due';
  // Cuando el plan efectivo (el que da la promo) y el contratado son el
  // mismo, "pasarás a X" no aporta nada: lo que cambia es que empieza a
  // cobrarse, no el plan.
  const mismoPlanTrasPromo =
    !!planEfectivo?.id_plan_efectivo &&
    planEfectivo.id_plan_efectivo === planEfectivo.id_plan_contratado;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-600 dark:border-gray-400" />
      </div>
    );
  }

  return (
    <>
    <motion.div
      className="w-full space-y-6 pb-28 lg:pb-0"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 tour-title"
      >
        <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          Suscripción
        </span>
      </motion.div>

      {/* Tour */}
      <Joyride
        steps={SubscriptionsTourSteps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        locale={{
          back: 'Atrás',
          close: 'Cerrar',
          last: 'Finalizar',
          next: 'Siguiente',
          skip: 'Saltar',
        }}
        callback={({ status }) => {
          if (status === 'finished' || status === 'skipped') setRunTour(false);
        }}
        styles={getTourStyles(isMobile)}
      />

      {/* ── Aviso: contratación deshabilitada ────────────────────────────────
          Sin este aviso la pantalla parece rota: los botones salen apagados
          y nada explica por qué. Solo se muestra cuando los flags ya han
          cargado, para no parpadear en cada entrada. */}
      {!cargandoFlags && !botonesActivos && (
        <motion.div variants={itemVariants}>
          <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3">
            <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                Contratación no disponible por el momento
              </span>
              <span className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                Puedes consultar los planes y tu suscripción, pero todavía no
                es posible contratar ni hacer cambios. Estará disponible muy
                pronto.
              </span>
            </div>
          </div>
        </motion.div>
      )}

      {/* ── Aviso de cobro fallido ───────────────────────────────────────────── */}
      {pagoFallido && (
        <motion.div variants={itemVariants}>
          <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-4 py-3">
            <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">
                No hemos podido cobrar tu última factura. Actualiza tu método de pago
                para no perder el acceso — se reintentará automáticamente los próximos días.
              </p>
              <button
                onClick={handleCambiarMetodoPago}
                disabled={cambiandoMetodoPago || !botonesActivos}
                className="text-xs font-semibold text-red-700 dark:text-red-300 underline mt-1 disabled:opacity-60"
              >
                {cambiandoMetodoPago ? 'Abriendo…' : 'Actualizar método de pago'}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* ── Plan activo ──────────────────────────────────────────────────────── */}
      <motion.div variants={itemVariants}>
        <Card className={`rounded-2xl ${GLASS_CARD} tour-current-plan`}>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <CreditCard className="w-5 h-5" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Plan activo
              </h3>
            </div>

            {!planActivo ? (
              esDiamante ? (
                // Sin días de promoción: su gratuidad no caduca. Pero sigue
                // eligiendo plan, que define funciones y cupo.
                <div className="flex items-start gap-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-xl px-4 py-4">
                  <div className="flex items-center justify-center shrink-0 bg-white dark:bg-white/10 rounded-lg w-12 h-12">
                    <Gem className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-cyan-800 dark:text-cyan-300">
                      Fundador Diamante — acceso gratuito de por vida
                    </p>
                    <p className="text-xs text-cyan-700 dark:text-cyan-300/90 leading-relaxed mt-1">
                      Tu plan no tiene coste, ahora ni nunca. Solo los trabajadores
                      adicionales se facturan a precio normal.
                    </p>
                    <p className="text-[11px] text-cyan-600/80 dark:text-cyan-400/70 mt-2">
                      Elige un plan abajo para activar tus funciones — seguirá siendo gratis.
                    </p>
                  </div>
                </div>
              ) : promo ? (
                <div className="flex items-start gap-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl px-4 py-4">
                  {(() => {
                    const diasRestantes = promo.fecha_fin
                      ? Math.max(
                          0,
                          Math.ceil(
                            (new Date(promo.fecha_fin).getTime() - new Date().setHours(0, 0, 0, 0)) /
                              (1000 * 60 * 60 * 24)
                          )
                        )
                      : null;

                    return diasRestantes != null ? (
                      <div className="flex flex-col items-center justify-center shrink-0 bg-white dark:bg-white/10 rounded-lg px-3 py-2 min-w-[68px]">
                        <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 leading-none">
                          {diasRestantes}
                        </span>
                        <span className="text-[10px] font-semibold text-emerald-600/80 dark:text-emerald-400/70 uppercase tracking-wide mt-0.5">
                          {diasRestantes === 1 ? 'día' : 'días'}
                        </span>
                      </div>
                    ) : (
                      <Gift className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    );
                  })()}
                  <div>
                    <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                      Todavía no has elegido plan — mientras tanto, todo es gratis
                    </p>
                    <p className="text-xs text-emerald-700 dark:text-emerald-300 leading-relaxed mt-1">
                      {formatearTextoPromocionEmpresa(promo)}
                    </p>
                    <p className="text-[11px] text-emerald-600/80 dark:text-emerald-400/70 mt-2">
                      Elige uno abajo para tenerlo listo cuando termine la promoción — no se te cobrará nada hasta entonces.
                    </p>
                  </div>
                </div>
              ) : (
                <div className={`flex flex-col items-center justify-center py-10 text-center ${INNER_ROW}`}>
                  <CreditCard className="w-8 h-8 text-gray-200 dark:text-gray-700 mb-2" />
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    No tienes ningún plan contratado todavía.
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Elige uno abajo, en "Planes disponibles".
                  </p>
                </div>
              )
            ) : (
              <>
                <div className={`flex items-center justify-between gap-3 ${INNER_ROW} px-4 py-4`}>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-2xl font-bold text-gray-900 dark:text-white">
                        {planEfectivo?.plan_efectivo ?? planActivo.plan}
                      </span>
                      {esPremium && (
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            esDiamante
                              ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300'
                              : 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300'
                          }`}
                        >
                          {esDiamante ? (
                            <Gem className="w-3 h-3 shrink-0" />
                          ) : (
                            <Award className="w-3 h-3 shrink-0" />
                          )}
                          Fundador {esDiamante ? 'Diamante' : 'Platino'}
                        </span>
                      )}
                    </div>

                    {/* Explica por qué el precio no es el de tarifa y que
                        el descuento no caduca. */}
                    {esPremium && (
                      <div
                        className={`flex items-start gap-1.5 text-xs ${
                          esDiamante
                            ? 'text-cyan-600 dark:text-cyan-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        <BadgeCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>
                          {esDiamante
                            ? 'Como Fundador Diamante, tu plan es gratuito de por vida.'
                            : 'Como Fundador Platino, tienes un 50% de descuento de por vida en tu plan.'}{' '}
                          Los trabajadores adicionales se facturan a precio normal.
                        </span>
                      </div>
                    )}

                    {/* Qué pasa al terminar la promoción. Con una baja
                        programada el mensaje es el contrario: no "pasarás
                        a X", sino que ese día se acaba. El Diamante no lo
                        ve — su plan ya es gratis de por vida. */}
                    {planEfectivo?.por_promocion && !esDiamante && (
                      bajaProgramada ? (
                        <div className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-500">
                          <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          <span>
                            Gratis por tu promoción
                            {planEfectivo.fecha_fin_promocion
                              ? ` hasta el ${formatFecha(planEfectivo.fecha_fin_promocion)}`
                              : ''}
                            {' '}— ese día tu plan termina y no se renovará
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-start gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
                          <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          <span>
                            Incluido en tu promoción
                            {planEfectivo.fecha_fin_promocion
                              ? ` hasta el ${formatFecha(planEfectivo.fecha_fin_promocion)}`
                              : ''}
                            {!planEfectivo.plan_contratado
                              ? ' — elige un plan para cuando termine'
                              : mismoPlanTrasPromo
                              ? ` — después se factura a ${(planActivo.precio_mensual ?? 0)
                                  .toFixed(2)
                                  .replace('.', ',')}€/mes`
                              : ` — después pasarás a ${planEfectivo.plan_contratado}`}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-xl font-bold text-[#288E7F]">
                      {precioMostrado.toFixed(2).replace('.', ',')} €
                    </span>
                    {(planActivoConDescuento || esDiamante) &&
                      planActivo.precio_mensual != null && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 line-through">
                          {planActivo.precio_mensual.toFixed(2)} €
                        </p>
                      )}
                    <p className="text-xs text-gray-400 dark:text-gray-500">/mes</p>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  {/* Estado: se calcula, no es un literal. Una baja
                      programada tiene que verse aunque haya promo, y un
                      impago manda sobre todo lo demás. */}
                  <div className={`flex items-center justify-between ${INNER_ROW} px-4 py-3`}>
                    <div className="flex items-center gap-2">
                      {pagoFallido ? (
                        <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                      ) : bajaProgramada ? (
                        <XCircle className="w-4 h-4 text-amber-500 shrink-0" />
                      ) : cambioPendiente ? (
                        <Calendar className="w-4 h-4 text-amber-500 shrink-0" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                      )}
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Estado
                      </span>
                    </div>
                    <span
                      className={`text-sm font-semibold ${
                        pagoFallido
                          ? 'text-red-600 dark:text-red-400'
                          : bajaProgramada || cambioPendiente
                          ? 'text-amber-600 dark:text-amber-500'
                          : 'text-emerald-600 dark:text-emerald-400'
                      }`}
                    >
                      {pagoFallido
                        ? 'Pago pendiente'
                        : bajaProgramada
                        ? 'Baja programada'
                        : cambioPendiente
                        ? 'Cambio programado'
                        : 'Activa'}
                    </span>
                  </div>

                  <div className={`flex items-center justify-between ${INNER_ROW} px-4 py-3`}>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#103254] dark:text-blue-400 shrink-0" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Fecha de inicio
                      </span>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {formatFecha(planActivo.fecha_inicio)}
                    </span>
                  </div>

                  {!esDiamante && planActivo.fecha_fin && (
                    <div className={`flex items-center justify-between ${INNER_ROW} px-4 py-3`}>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#103254] dark:text-blue-400 shrink-0" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {/* ORDEN IMPORTANTE: la baja manda sobre todo lo
                              demás, y el cambio de plan sobre la promo. Si
                              se evalúa por_promocion primero, un plan
                              cancelado sigue anunciando "Pasa a X". */}
                          {bajaProgramada
                            ? 'Mantiene el plan hasta'
                            : cambioPendiente
                            ? `Pasa a ${planActivo.nombre_plan_pendiente}`
                            : planEfectivo?.por_promocion
                            ? !planEfectivo.plan_contratado
                              ? 'Termina la promoción'
                              : mismoPlanTrasPromo
                              ? 'Empieza a facturarse'
                              : `Pasa a ${planEfectivo.plan_contratado}`
                            : 'Próximo cobro'}
                        </span>
                      </div>
                      <div className="text-right">
                        <span
                          className={`text-sm ${
                            bajaProgramada || cambioPendiente
                              ? 'text-amber-600 dark:text-amber-500 font-medium'
                              : 'text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {formatFecha(planActivo.fecha_fin)}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className={`flex items-center justify-between ${INNER_ROW} px-4 py-3`}>
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-[#103254] dark:text-blue-400 shrink-0" />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                          Auto-renovación
                        </span>
                        {/* Sin el `!promo` de antes: durante la promoción
                            la baja programada también existe y hay que
                            avisarla. */}
                        {!esDiamante && bajaProgramada && (
                          <span className="text-[11px] text-amber-600 dark:text-amber-500">
                            Baja programada — reactívala para poder cambiar de plan
                          </span>
                        )}
                      </div>
                    </div>
                    {cambiandoAutoRenovacion ? (
                      <div className="w-9 h-5 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#103254] dark:border-blue-400" />
                      </div>
                    ) : (
                      <Switch
                        checked={planActivo.auto_renovacion}
                        disabled={
                          !botonesActivos ||
                          esDiamante ||
                          !planActivo.id_suscripcion_stripe
                        }
                        onCheckedChange={handleToggleAutoRenovacion}
                      />
                    )}
              </div>
            </div>
              </>
            )}

            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Button
                variant="outline"
                onClick={handleCambiarMetodoPago}
                disabled={cambiandoMetodoPago || !botonesActivos}
                className="flex-1 rounded-lg dark:bg-white/5 dark:border-white/10 dark:text-white"
              >
                <CreditCard className="w-3.5 h-3.5 mr-2" />
                {cambiandoMetodoPago ? 'Abriendo…' : 'Cambiar método de pago'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setMostrarHistorial(true)}
                className="flex-1 rounded-lg dark:bg-white/5 dark:border-white/10 dark:text-white"
              >
                <FileText className="w-3.5 h-3.5 mr-2" />
                Ver historial de pagos
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* ── Trabajadores facturados ──────────────────────────────────────────── */}
      {planActivo && (
        <motion.div variants={itemVariants}>
          <Card className={`rounded-2xl ${GLASS_CARD} tour-trabajadores-facturacion`}>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Users className="w-5 h-5" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Trabajadores
                </h3>
              </div>

              <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                {bajaProgramada
                  ? 'Tu plan tiene una baja programada — no se pueden habilitar trabajadores hasta que lo reactives.'
                  : !planPermiteTrabajadorAdicional
                  ? `Tu plan contratado${planEfectivo?.plan_contratado ? ` (${planEfectivo.plan_contratado})` : ''} no incluye trabajadores adicionales. Cambia de plan para poder habilitarlos.`
                  : promo
                  ? `Los trabajadores que habilites son gratuitos hasta el ${formatFecha(planEfectivo?.fecha_fin_promocion)}. A partir de esa fecha se facturarán según tu plan contratado${planEfectivo?.plan_contratado ? ` (${planEfectivo.plan_contratado})` : ''}.`
                  : 'Habilita el acceso de cada trabajador cuando lo necesites.'}
              </p>

              {/* Listado con switch de acceso */}
              <div className="pt-2 space-y-2">
                {cargandoLista ? (
                  <div className="flex justify-center py-6">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-400" />
                  </div>
                ) : listaTrabajadores.length === 0 ? (
                  <div className="flex flex-col items-center gap-3 py-4">
                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                      Todavía no tienes trabajadores dados de alta.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/panel-empresa/workers')}
                      className="rounded-lg dark:bg-white/5 dark:border-white/10 dark:text-white"
                    >
                      <Users className="w-3.5 h-3.5 mr-2" />
                      Ir a Trabajadores
                    </Button>
                  </div>
                ) : (
                  (() => {
                    const cupoTotal = planActivo?.trabajadores_incluidos ?? 0;
                    const incluidosActuales = listaTrabajadores.filter((t) => t.es_incluido_plan).length;

                    const mostrarGratisParaTodos =
                      incluidosActuales === 0 && cupoTotal > 0;

                    const cupoLibre = Math.max(0, cupoTotal - incluidosActuales);

                    return listaTrabajadores.map((trabajador) => {
                      const esSinContratar =
                        !trabajador.es_incluido_plan &&
                        !trabajador.cubierto_por_promo &&
                        !trabajador.tiene_acceso;

                      const seriaGratisSiSeActiva =
                        mostrarGratisParaTodos || (esSinContratar && cupoLibre > 0);

                      return (
                        <div
                          key={trabajador.id_usuario}
                          ref={(el) => {
                            trabajadorRefs.current[trabajador.id_usuario] = el;
                          }}
                          className={`flex items-center justify-between gap-3 ${INNER_ROW} px-4 py-3 transition-all duration-500 ${
                            trabajadorResaltado === trabajador.id_usuario
                              ? 'ring-2 ring-[#288E7F] ring-offset-2 dark:ring-offset-[#1c1c1e] bg-emerald-50/50 dark:bg-emerald-900/10'
                              : ''
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
                              {trabajador.nombre || trabajador.email}
                            </p>

                            <div className="flex items-center gap-1.5 mt-0.5">
                              {trabajador.cancelacion_programada ? (
                                <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-full">
                                  Baja programada
                                </span>
                              ) : trabajador.es_incluido_plan ? (
                                <span className="text-[10px] font-semibold text-[#103254] dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded-full">
                                  Incluido en el plan
                                </span>
                              ) : trabajador.cubierto_por_promo ? (
                                // tiene_acceso no vale como criterio: en promoción es
                                // true para todos, incluso sin contratar.
                                ['trialing', 'active', 'past_due'].includes(
                                  trabajador.stripe_status ?? ''
                                ) ? (
                                  <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded-full">
                                    Contratado · Gratis hasta el {formatFecha(planEfectivo?.fecha_fin_promocion)}
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-semibold text-[#103254] dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded-full">
                                    Cubierto por la promoción hasta el {formatFecha(planEfectivo?.fecha_fin_promocion)}
                                  </span>
                                )
                              ) : (
                                <span
                                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                    trabajador.tiene_acceso
                                      ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                                      : trabajador.fecha_fin_acceso
                                        ? 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
                                        : 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/10'
                                  }`}
                                >
                                  {trabajador.tiene_acceso
                                    ? 'Facturando'
                                    : trabajador.fecha_fin_acceso
                                      ? 'Suscripción finalizada'
                                      : 'Sin contratar'}
                                </span>
                              )}
                            </div>

                            {trabajador.cancelacion_programada && trabajador.fecha_fin_acceso ? (
                              <p className="text-[11px] text-amber-600 dark:text-amber-500 mt-1">
                                Mantiene acceso hasta el {formatFecha(trabajador.fecha_fin_acceso)}
                              </p>
                            ) : trabajador.cubierto_por_promo ? (
                              // Solo al ya contratado. Si su plan no vende trabajadores,
                              // se avisa de que perderá el acceso, no solo del cobro.
                              ['trialing', 'active', 'past_due'].includes(
                                trabajador.stripe_status ?? ''
                              ) ? (
                                planPermiteTrabajadorAdicional ? (
                                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1 leading-relaxed">
                                    La promoción cubre su acceso hasta esa fecha. A partir de
                                    entonces se facturará con normalidad según tu plan.
                                  </p>
                                ) : (
                                  <p className="text-[11px] text-amber-600 dark:text-amber-500 mt-1 leading-relaxed">
                                    La promoción cubre su acceso hasta esa fecha, sin coste. Cuando
                                    termine, tu plan
                                    {planEfectivo?.plan_contratado ? ` ${planEfectivo.plan_contratado}` : ''} no
                                    incluye trabajadores, así que para conservarlo tendrás que
                                    cambiar a un plan que sí los cubra.
                                  </p>
                                )
                              ) : null
                            ) : (
                              <>
                                {trabajador.tiene_acceso && trabajador.fecha_fin_acceso ? (
                                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                                    Próximo cobro: {formatFecha(trabajador.fecha_fin_acceso)}
                                  </p>
                                ) : !trabajador.tiene_acceso && trabajador.fecha_fin_acceso ? (
                                  <p className="text-[11px] text-red-600 dark:text-red-400 mt-1">
                                    Finalizó el {formatFecha(trabajador.fecha_fin_acceso)}
                                  </p>
                                ) : (
                                  trabajador.fecha_alta && (
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                                      Habilitado el {formatFecha(trabajador.fecha_alta)}
                                    </p>
                                  )
                                )}
                              </>
                            )}
                          </div>

                          <div className="flex items-center gap-3 shrink-0">
                            <span
                              className={`text-xs font-semibold whitespace-nowrap ${
                                trabajador.es_incluido_plan ||
                                trabajador.cubierto_por_promo ||
                                seriaGratisSiSeActiva
                                  ? 'text-emerald-600 dark:text-emerald-400'
                                  : trabajador.tiene_acceso
                                  ? 'text-[#103254] dark:text-blue-400'
                                  : 'text-gray-300 dark:text-gray-600'
                              }`}
                            >
                              {trabajador.es_incluido_plan || seriaGratisSiSeActiva
                                ? 'Gratis'
                                : !planPermiteTrabajadorAdicional
                                ? 'No disponible'
                                : `${(planActivo?.precio_trabajador_adicional ?? 0).toFixed(2).replace('.', ',')}€/mes`}
                            </span>

                            {cambiandoAcceso === trabajador.id_usuario ? (
                              <div className="w-9 h-5 flex items-center justify-center shrink-0">
                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#103254] dark:border-blue-400" />
                              </div>
                            ) : (
                              <Switch
                                checked={
                                  (
                                    trabajador.es_incluido_plan ||
                                    ['trialing', 'active', 'past_due'].includes(trabajador.stripe_status ?? '')
                                  ) &&
                                  !trabajador.cancelacion_programada
                                }
                                disabled={(() => {
                                  // Contratado = ocupa cupo o tiene suscripción viva.
                                  // tiene_acceso no vale: en promo es true para todos.
                                  const yaContratado =
                                    (trabajador.es_incluido_plan ||
                                      ['trialing', 'active', 'past_due'].includes(
                                        trabajador.stripe_status ?? ''
                                      )) &&
                                    !trabajador.cancelacion_programada;

                                  return (
                                    // Interruptor global de la pantalla.
                                    !botonesActivos ||
                                    (!!cambiandoAcceso && cambiandoAcceso !== trabajador.id_usuario) ||
                                    // Si no está contratado, solo se puede habilitar
                                    // cuando el plan CONTRATADO cubre trabajadores.
                                    (!yaContratado && !planPermiteTrabajadorAdicional) ||
                                    // Ni tampoco si el plan tiene un cambio programado,
                                    // una baja programada, o ya está cancelado en Stripe.
                                    (!yaContratado &&
                                      (bajaProgramada ||
                                        cambioPendiente ||
                                        planActivo?.stripe_status === 'canceled'))
                                  );
                                })()}
                                onCheckedChange={(checked) =>
                                  handleToggleAcceso(trabajador, checked)
                                }
                              />
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
      {/* ── Resumen de precio ─────────────────────────────────────────────────── */}
      {planActivo && (
        <motion.div variants={itemVariants}>
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <CreditCard className="w-5 h-5" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Resumen de precio
                </h3>
              </div>

              {/* Desglose de precio: plan + trabajadores facturados = total */}
              {(() => {
                // Solo los que se cobran ya: fuera de trial. Los que están en
                // 'trialing' los cubre la promoción, no suman este mes.
                const trabajadoresFacturando = listaTrabajadores.filter(
                  (t) =>
                    !t.es_incluido_plan &&
                    ['active', 'past_due'].includes(t.stripe_status ?? '')
                ).length;
                const precioPorTrabajador = planActivo.precio_trabajador_adicional ?? 0;
                const subtotalTrabajadores = trabajadoresFacturando * precioPorTrabajador;
                const precioPlan = precioMostrado;
                const total = precioPlan + subtotalTrabajadores;

                const mesActual = new Date().toLocaleDateString('es-ES', {
                  month: 'long',
                  year: 'numeric',
                });

                return (
                  <div className={`${INNER_ROW} px-4 py-3 space-y-1.5`}>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 capitalize">
                      {mesActual}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>Plan {planActivo.plan}</span>
                      <span>{precioPlan.toFixed(2).replace('.', ',')}€/mes</span>
                    </div>
                    {trabajadoresFacturando > 0 && (
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>
                          {trabajadoresFacturando} trabajador{trabajadoresFacturando > 1 ? 'es' : ''} ×{' '}
                          {precioPorTrabajador.toFixed(2).replace('.', ',')}€
                        </span>
                        <span>{subtotalTrabajadores.toFixed(2).replace('.', ',')}€/mes</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm font-bold text-gray-900 dark:text-white pt-1.5 border-t border-gray-100 dark:border-white/10">
                      <span>Total</span>
                      <span className="text-[#288E7F]">{total.toFixed(2).replace('.', ',')}€/mes</span>
                    </div>
                  </div>
                );
              })()}

              {/* Si hay algo programado (baja de plan, bajada, trabajadores
                  con baja programada, o una promo activa cuyo cupón dejará
                  de aplicar), se muestra también cómo queda el total a
                  partir de esa fecha.
                  Un Fundador Diamante entra al bloque si tiene trabajadores
                  facturables tras la promo — su plan sigue a 0€, pero esos
                  trabajadores sí se cobrarán al terminar el cupón. */}
              {(!esDiamante ||
                listaTrabajadores.some(
                  (t) =>
                    !t.es_incluido_plan &&
                    ['trialing', 'active', 'past_due'].includes(t.stripe_status ?? '')
                )) &&
                (bajaProgramada ||
                  cambioPendiente ||
                  !!promo ||
                  listaTrabajadores.some((t) => t.cancelacion_programada)) &&
                (() => {
                  const hayPlanFuturo = cambioPendiente;
                  const planSeCancela = bajaProgramada && !hayPlanFuturo;

                  // Fundador Diamante: el plan es gratuito de por vida, así
                  // que su precio futuro es 0€ pase lo que pase con la promo.
                  // El descuento de fundador (Diamante 100% → 0, Platino
                  // 50%) se aplica de por vida, también al precio que
                  // regirá tras la promo. Diamante queda en 0; Platino, a
                  // mitad de precio; el resto, precio completo.
                  const precioPlanBase = planSeCancela
                    ? 0
                    : hayPlanFuturo
                    ? planActivo.precio_mensual_pendiente ?? 0
                    : planActivo.precio_mensual;

                  const precioPlanFuturo = aplicarDescuentoFundador(precioPlanBase);

                  const permiteTrabajadoresFuturo = planSeCancela
                    ? false
                    : hayPlanFuturo
                    ? (planActivo.precio_trabajador_adicional_pendiente ?? 0) > 0
                    : true;

                  // Los que ya facturan y NO tienen baja programada individual
                  // siguen facturando igual tras el cambio — salvo que el
                  // plan futuro no venda trabajadores, en cuyo caso se
                  // cancelan todos.
                  // Contratados de verdad, no los que solo tienen acceso por
                  // la promoción. Aquí sí cuentan los de trial: ya se cobrarán.
                  const trabajadoresFuturo = permiteTrabajadoresFuturo
                    ? listaTrabajadores.filter(
                        (t) =>
                          !t.es_incluido_plan &&
                          ['trialing', 'active', 'past_due'].includes(t.stripe_status ?? '') &&
                          !t.cancelacion_programada
                      ).length
                    : 0;

                  const precioPorTrabajadorFuturo = hayPlanFuturo
                    ? planActivo.precio_trabajador_adicional_pendiente ?? 0
                    : planActivo.precio_trabajador_adicional ?? 0;

                  const totalFuturo =
                    precioPlanFuturo + trabajadoresFuturo * precioPorTrabajadorFuturo;

                  // Si el bloque se dispara solo por la promo (sin baja de
                  // plan ni cambio pendiente), la fecha relevante es el fin
                  // de promo — que es hasta cuándo todo es gratis, y lo que
                  // ven los trabajadores. Solo cuando hay baja/cambio real
                  // manda la fecha_fin del plan.
                  const hayBajaOCambio =
                    bajaProgramada ||
                    cambioPendiente ||
                    listaTrabajadores.some((t) => t.cancelacion_programada);

                  const fechaEvento =
                    !hayBajaOCambio && planEfectivo?.por_promocion
                      ? planEfectivo.fecha_fin_promocion
                      : planActivo.fecha_fin;

                  const fecha = fechaEvento ? formatFecha(fechaEvento) : null;

                  return (
                    <div className={`${INNER_ROW} px-4 py-3 space-y-1.5 border-amber-200 dark:border-amber-800`}>
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                        A partir del {fecha ?? 'próximo cambio'}
                      </p>
                      {planSeCancela ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Sin plan contratado — 0,00€/mes
                        </p>
                      ) : (
                        <>
                          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>
                              {/* Con bajada programada manda el plan pendiente. */}
                              {hayPlanFuturo
                                ? `Plan ${planActivo.nombre_plan_pendiente}`
                                : planEfectivo?.por_promocion
                                ? planEfectivo.plan_contratado
                                  ? `Plan ${planEfectivo.plan_contratado}`
                                  : 'Sin plan contratado'
                                : `Plan ${planActivo.plan}`}
                            </span>
                            <span>{precioPlanFuturo.toFixed(2).replace('.', ',')}€/mes</span>
                          </div>
                          {trabajadoresFuturo > 0 && (
                            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                              <span>
                                {trabajadoresFuturo} trabajador{trabajadoresFuturo > 1 ? 'es' : ''} ×{' '}
                                {precioPorTrabajadorFuturo.toFixed(2).replace('.', ',')}€
                              </span>
                              <span>
                                {(trabajadoresFuturo * precioPorTrabajadorFuturo).toFixed(2).replace('.', ',')}€/mes
                              </span>
                            </div>
                          )}
                        </>
                      )}
                      <div className="flex items-center justify-between text-sm font-bold text-amber-700 dark:text-amber-400 pt-1.5 border-t border-amber-200/50 dark:border-amber-800/50">
                        <span>Nuevo total</span>
                        <span>{totalFuturo.toFixed(2).replace('.', ',')}€/mes</span>
                      </div>
                    </div>
                  );
                })()}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* ── Planes disponibles ────────────────────────────────────────────────── */}
      {planes.length > 0 && (
        <motion.div variants={itemVariants}>
          <Card className={`rounded-2xl ${GLASS_CARD} tour-plan-catalog`}>
            <CardContent className="p-6 space-y-5">
              {/* Título */}
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Tag className="w-5 h-5" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Planes disponibles
                </h3>
              </div>

              {/* Con una baja programada, cambiar de plan queda bloqueado:
                  el cambio heredaría la cancelación (plan nuevo cancelándose
                  igual) o la borraría sin decisión del cliente. Se explica
                  aquí, no solo en el botón deshabilitado. */}
              {bajaProgramada && !esDiamante && (
                <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3">
                  <XCircle className="w-4 h-4 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                      Tu plan tiene una baja programada, así que no puedes cambiar de plan
                      ahora mismo.
                    </p>
                    <p className="text-[11px] text-amber-600/80 dark:text-amber-400/70 mt-1">
                      Reactívalo desde "Auto-renovación" y después elige el plan que quieras.
                    </p>
                  </div>
                </div>
              )}

              {/* Promo activa de ESTA empresa. Los fundadores ven su propia
                  condición: una promo con fecha de fin les confundiría. */}
              {esDiamante ? (
                <div className="flex items-start gap-3 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-xl px-4 py-3">
                  <BadgeCheck className="w-4 h-4 text-cyan-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-cyan-800 dark:text-cyan-300 leading-relaxed">
                      Como Fundador Diamante, cualquier plan que elijas es gratuito de por vida.
                    </p>
                    <p className="text-[11px] text-cyan-600/80 dark:text-cyan-400/70 mt-1">
                      Los trabajadores adicionales se facturan a precio normal.
                    </p>
                  </div>
                </div>
              ) : esPlatino ? (
                <div className="flex items-start gap-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl px-4 py-3">
                  <BadgeCheck className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                      Como Fundador Platino, tienes un 50% de descuento de por vida en el plan que elijas.
                    </p>
                    {promo && (
                      <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-1">
                        {formatearTextoPromocionEmpresa(promo)}
                      </p>
                    )}
                  </div>
                </div>
              ) : null}

              {promo && !esDiamante && !esPlatino && (
                <div className="flex items-start gap-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl px-4 py-3">
                  <Gift className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-emerald-700 dark:text-emerald-300 leading-relaxed">
                      {formatearTextoPromocionEmpresa(promo)}
                    </p>
                    <p className="text-[11px] text-emerald-600/80 dark:text-emerald-400/70 mt-1">
                      Fecha de inicio: {formatFecha(promo.fecha_inicio)}
                      {' · '}
                      Fecha fin: {promo.fecha_fin ? formatFecha(promo.fecha_fin) : 'sin fecha de fin'}
                    </p>
                  </div>
                </div>
              )}

              {/* Grid de cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {planes.map((plan, idx) => (
                  <PlanCard
                    key={plan.id_plan}
                    plan={plan}
                    esActivo={planActivo?.id_plan === plan.id_plan}
                    isDestacado={idx === planes.length - 1}
                    promo={promo}
                    empresaId={empresaId}
                    open={funcionalidadesAbiertas}
                    onToggleOpen={() => setFuncionalidadesAbiertas((v) => !v)}
                    idPlanPendiente={planActivo?.id_plan_pendiente ?? null}
                    nombrePlanPendiente={planActivo?.nombre_plan_pendiente ?? null}
                    fechaCambioPendiente={planActivo?.fecha_fin ?? null}
                    onCambiado={refrescarTodo}
                    planActivo={planActivo}
                    descuentoFundadorPct={descuentoFundadorPct}
                    bajaProgramada={bajaProgramada}
                    botonesActivos={botonesActivos}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

    </motion.div>

      <HistorialPagosPanel
        open={mostrarHistorial}
        onClose={() => setMostrarHistorial(false)}
        comprobantes={comprobantes}
        movimientos={movimientos}
        loading={loadingComprobante}
        onDescargar={descargarComprobante}
        onRefrescar={fetchComprobantes}
        onRefrescarMovimientos={fetchMovimientos}
      />
    </>
  );
}